import {parseUnifiedDiff} from '@github-ui/agent-sessions/utils/parse-unified-diff'
import type {RuleSeverity} from '@github-ui/code-scanning-shared/types/rule-severity'
import type {Comment, DiffEntry} from '@github-ui/diff-lines/conversation-types'
import type {FindingEntity} from './types'

/** Display order for severity levels (most severe first) */
export const SEVERITY_ORDER: RuleSeverity[] = [
  'failure',
  'critical',
  'high',
  'error',
  'medium',
  'warning',
  'low',
  'note',
  'nit',
  'notice',
  'none',
]

const getSeveritySortIndex = (severity: RuleSeverity | undefined): number => {
  if (!severity) {
    return SEVERITY_ORDER.length
  }

  const index = SEVERITY_ORDER.indexOf(severity)
  return index === -1 ? SEVERITY_ORDER.length : index
}

export const sortFindingsBySeverity = (findings: FindingEntity[]): FindingEntity[] => {
  return [...findings].sort((a, b) => {
    const severityA = getEntitySeverity(a)
    const severityB = getEntitySeverity(b)
    const indexA = getSeveritySortIndex(severityA)
    const indexB = getSeveritySortIndex(severityB)
    return indexA - indexB
  })
}

export const getEntityCompositeID = (finding: FindingEntity): string => {
  return finding.entityType + finding.entity.id
}

export const getEntitySeverity = (finding: FindingEntity): RuleSeverity | undefined => {
  switch (finding.entityType) {
    case 'annotation':
      return finding.entity.codeScanningDetails?.ruleSeverity ?? finding.entity.annotationLevel
    case 'automated_review_comment':
      return finding.entity.severity
  }
}

export const getEntityComment = (finding: FindingEntity & {entityType: 'automated_review_comment'}): Comment => {
  // The comment field is serialized from Rails without Relay fragment spreads.
  // Cast to Comment since AutomatedReviewThreadComment expects it, but the
  // fragment spread field is never accessed at runtime.
  return finding.entity.comment as unknown as Comment
}

export const getEntityDiffContent = (finding: FindingEntity): DiffEntry[] => {
  if (finding.entityType === 'annotation') {
    const files = finding.entity.codeScanningDetails?.suggestedFix?.files
    if (files) {
      return files
        .filter((file): file is {filePath: string; diffContent: string} => !!file.filePath && !!file.diffContent)
        .map(file => ({
          path: file.filePath,
          diffLines: parseUnifiedDiff(file.diffContent),
        }))
    }
  } else if (finding.entityType === 'automated_review_comment') {
    const files = finding.entity.suggestion?.files
    if (files) {
      return files
        .filter(file => file.diffContent)
        .map(file => ({
          path: file.path,
          diffLines: parseUnifiedDiff(file.diffContent),
        }))
    }
  }
  return []
}

export const getEntityMessageHtml = (finding: FindingEntity): string | null => {
  switch (finding.entityType) {
    case 'annotation':
      return finding.entity.codeScanningDetails?.messageHtml ?? null
    case 'automated_review_comment':
      return finding.entity.messageHtml
  }
}

export const getEntitySuggestedFixDescriptionHtml = (finding: FindingEntity): string | null => {
  switch (finding.entityType) {
    case 'annotation':
      return finding.entity.codeScanningDetails?.suggestedFixDescriptionHtml ?? null
    case 'automated_review_comment':
      return null
  }
}

const SOURCE_LABELS: Record<string, string> = {
  actions: 'Actions',
  general: 'Actions', // TEMP: 'general' means 'actions' for a minute here
  copilot: 'Copilot',
  code_scanning: 'CodeQL',
  dependabot: 'Dependabot',
}

export const getEntitySource = (finding: FindingEntity): string => {
  const raw = finding.entity.source
  return SOURCE_LABELS[raw] ?? (finding.entityType === 'annotation' ? (finding.entity.toolName ?? raw) : raw)
}

export const getEntityFileLocation = (finding: FindingEntity): string | null => {
  switch (finding.entityType) {
    case 'annotation': {
      const {filename, startLine, endLine} = finding.entity
      if (!filename) return null
      return startLine === endLine ? `${filename}:${startLine}` : `${filename}:${startLine}-${endLine}`
    }
    case 'automated_review_comment': {
      const {path, line} = finding.entity
      if (!path) return null
      return line ? `${path}:${line}` : path
    }
  }
}
